import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router";

type NavLink = {
  label: string;
  href: string;
};

const navLinks: NavLink[] = [
  { label: "Accueil", href: "/" },
  { label: "Services", href: "/services" },
  { label: "Blog", href: "/blog" },
  { label: "Portfolio", href: "/portfolio" },
  { label: "Contact", href: "/contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navbarContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const accentColor = "#00ff41";
  const inactiveNavColor = "rgba(255,255,255,0.65)";

  useEffect(() => {
    let rafId = 0;
    let isTicking = false;

    const updateScrolledState = () => {
      const shouldBeScrolled = window.scrollY > 40;
      setScrolled((currentValue) =>
        currentValue === shouldBeScrolled ? currentValue : shouldBeScrolled,
      );
      isTicking = false;
    };

    const onScroll = () => {
      if (isTicking) return;
      isTicking = true;
      rafId = window.requestAnimationFrame(updateScrolledState);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;

    const handleOutsidePointerDown = (pointerEvent: PointerEvent) => {
      const clickedNode = pointerEvent.target as Node;
      if (!navbarContainerRef.current?.contains(clickedNode)) {
        setMobileOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown);
    };
  }, [mobileOpen]);

  const handleLink = (link: NavLink) => {
    setMobileOpen(false);
    if (link.href === "/") {
      navigate("/", { replace: true });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    navigate(link.href);
  };

  const handleCTA = () => {
    setMobileOpen(false);
    navigate("/contact");
  };

  const isActive = (link: NavLink) => {
    if (link.href === "/") return location.pathname === "/";
    if (link.href === "/portfolio") return location.pathname.startsWith("/portfolio");
    return location.pathname === link.href;
  };

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 px-6 py-4 pointer-events-none"
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      <div
        ref={navbarContainerRef}
        className="w-full max-w-[900px] mx-auto rounded-2xl transition-all duration-500 overflow-hidden pointer-events-auto"
        style={{
          background: "rgba(8, 8, 8, 0.85)",
          backdropFilter: scrolled ? "blur(12px)" : "none",
          border: "1px solid rgba(0, 255, 65, 0.2)",
        }}
      >
        <div className="flex items-center justify-between px-5 py-3">
          {/* Logo */}
          <button
            onClick={() => {
              navigate("/", { replace: true });
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-2 group"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #00ff41, #00cc33)",
                boxShadow: "0 0 16px rgba(0,255,65,0.4)",
              }}
            >
              <img
                src="/logo.webp"
                alt="Logo Awaken Code"
                width={22}
                height={22}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                style={{
                  width: "22px",
                  height: "22px",
                  objectFit: "contain",
                }}
              />
            </div>
            <span
              style={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 700,
                fontSize: "1.1rem",
                color: "#fff",
                letterSpacing: "-0.02em",
              }}
            >
              Awaken<span style={{ color: accentColor }}>Code</span>
            </span>
          </button>

          {/* Desktop Links */}
          <div className="hidden min-[861px]:flex items-center gap-8">
            {navLinks.map((link) => {
              const active = isActive(link);
              return (
                <button
                  key={link.label}
                  onClick={() => handleLink(link)}
                  className="relative group"
                  style={{
                    color: active ? accentColor : inactiveNavColor,
                    fontSize: "0.875rem",
                    fontWeight: active ? 600 : 500,
                    letterSpacing: "0.01em",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    fontFamily: "'Space Grotesk', sans-serif",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = accentColor)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = active
                      ? accentColor
                      : inactiveNavColor)
                  }
                >
                  {link.label}
                  <span
                    className="absolute -bottom-1 left-0 h-px transition-all duration-300"
                    style={{
                      width: active ? "100%" : "0%",
                      background: `linear-gradient(90deg, ${accentColor}, transparent)`,
                    }}
                  />
                  <span
                    className="absolute -bottom-1 left-0 w-0 h-px group-hover:w-full transition-all duration-300"
                    style={{
                      background: `linear-gradient(90deg, ${accentColor}, transparent)`,
                    }}
                  />
                </button>
              );
            })}
          </div>

          {/* CTA */}
          <div className="hidden min-[861px]:flex items-center gap-3">
            <button
              onClick={handleCTA}
              className="px-5 py-2 rounded-xl transition-all duration-300"
              style={{
                background: "linear-gradient(135deg, #00ff41, #00cc33)",
                color: "#000",
                fontWeight: 600,
                fontSize: "0.875rem",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 0 20px rgba(0,255,65,0.3)",
                fontFamily: "'Space Grotesk', sans-serif",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 0 30px rgba(0,255,65,0.6)";
                (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 0 20px rgba(0,255,65,0.3)";
                (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
              }}
            >
              Démarrer un projet
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            type="button"
            aria-label={mobileOpen ? "Fermer le menu principal" : "Ouvrir le menu principal"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation-menu"
            className="min-[861px]:hidden flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{
              background: mobileOpen ? "rgba(0,255,65,0.1)" : "rgba(255,255,255,0.05)",
              border: mobileOpen ? "1px solid rgba(0,255,65,0.25)" : "1px solid rgba(255,255,255,0.08)",
              cursor: "pointer",
              color: mobileOpen ? "#00ff41" : "#fff",
            }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {mobileOpen ? (
                <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }} style={{ display: "flex" }}>
                  <X size={18} />
                </motion.span>
              ) : (
                <motion.span key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }} style={{ display: "flex" }}>
                  <Menu size={18} />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>

        <AnimatePresence initial={false}>
          {mobileOpen && (
            <motion.div
              id="mobile-navigation-menu"
              key="mobile-menu"
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              style={{ overflow: "hidden" }}
            >
              <div style={{ height: "1px", margin: "0 1.25rem", background: "linear-gradient(90deg, transparent, rgba(0,255,65,0.2), transparent)" }} />
              <div className="p-4 min-[861px]:hidden" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {navLinks.map((link) => {
                  const active = isActive(link);
                  return (
                    <button
                      key={link.label}
                      onClick={() => handleLink(link)}
                      className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-xl transition-all duration-200 hover:bg-white/5 hover:text-white"
                      style={{ background: active ? "rgba(0,255,65,0.07)" : "transparent", border: active ? "1px solid rgba(0,255,65,0.15)" : "1px solid transparent", color: active ? "#00ff41" : "rgba(255,255,255,0.75)", fontSize: "0.95rem", fontWeight: active ? 600 : 500, cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      <span style={{ width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0, background: active ? "#00ff41" : "rgba(255,255,255,0.15)", boxShadow: active ? "0 0 6px #00ff41" : "none", transition: "all 0.2s" }} />
                      {link.label}
                    </button>
                  );
                })}
                <button onClick={handleCTA} className="w-full mt-2 py-3 rounded-xl transition-all duration-300" style={{ background: "linear-gradient(135deg, #00ff41, #00cc33)", color: "#000", fontWeight: 700, fontSize: "0.95rem", border: "none", cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif", boxShadow: "0 0 20px rgba(0,255,65,0.35)", letterSpacing: "0.01em" }}>
                  Démarrer un projet
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}